import React, {
  InputHTMLAttributes,
  memo,
  useEffect,
  useRef,
  useState,
} from "react";

import { useField } from "@unform/core";

import {
  ArrowDownIcon,
  ErrorMessage,
  InputComponent,
  ListOptions,
  ListOptionsContainer,
  ListOptionsWrapper,
  Option,
  SelectContainer,
  SelectField,
  WrapperSelectContainer,
} from "./styles";

export interface IOptionsProps {
  value: string;
  text: string;
  disabled?: boolean;
}

interface IInputProps extends InputHTMLAttributes<HTMLInputElement> {
  options: IOptionsProps[];
  onChangeCustom?: (
    option: IOptionsProps,
    currentOption?: IOptionsProps,
  ) => void;
  disableFormInitialData?: boolean;
}

const Select: React.FC<IInputProps> = ({
  name,
  options,
  disabled,
  className,
  placeholder,
  onChangeCustom,
  disableFormInitialData,
  defaultValue: inputDefaultValue,
  ...rest
}: IInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const { fieldName, defaultValue, registerField, error, clearError } =
    useField(name);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [optionSelected, setOptionSelected] = useState<IOptionsProps>({
    text: "",
    value: defaultValue || inputDefaultValue,
  });

  const handleOpenDropDown = () => setIsOpen((prevState) => !prevState);
  const closeDropDown = () => setIsOpen(false);

  const handleSelectOption = (option: IOptionsProps) => {
    if (onChangeCustom) onChangeCustom(option, optionSelected);
    setOptionSelected(option);
    setIsOpen(false);
  };

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef,
      getValue: (ref) => {
        return ref.current.value;
      },
      setValue: (ref, value) => {
        ref.current.value = value;
      },
      clearValue: (ref) => {
        ref.current.value = "";
      },
    });
  }, [fieldName, registerField]);

  useEffect(() => {
    if (options.length) {
      const [option] = options.filter(
        (option) => option.value === optionSelected?.value,
      );

      setOptionSelected(option || options[0]);
    }
  }, [options]);

  useEffect(() => {
    if (options.length) {
      const [option] = options.filter(
        (option) =>
          option.value ===
          (!disableFormInitialData
            ? defaultValue || inputDefaultValue
            : inputDefaultValue),
      );

      setOptionSelected(option);
    }
  }, [defaultValue, inputDefaultValue]);

  return (
    <WrapperSelectContainer>
      <SelectContainer className={className} onMouseLeave={closeDropDown}>
        <InputComponent
          readOnly
          id={fieldName}
          ref={inputRef}
          placeholder={placeholder}
          onClick={handleOpenDropDown}
          value={optionSelected?.value}
          autoComplete="do-not-autofill"
          disabled={disabled || options?.length <= 1}
          {...rest}
        />

        <SelectField
          readOnly
          onFocus={clearError}
          placeholder={placeholder}
          value={optionSelected?.text}
          onClick={handleOpenDropDown}
          autoComplete="do-not-autofill"
          disabled={disabled || options?.length <= 1}
        />

        {!isOpen && <ArrowDownIcon />}

        {isOpen && (
          <ListOptionsContainer>
            <ListOptionsWrapper>
              <ListOptions onMouseLeave={closeDropDown}>
                {options?.map((option) => (
                  <Option
                    key={option.value}
                    disabled={option?.disabled}
                    onClick={() => handleSelectOption(option)}
                  >
                    {option.text}
                  </Option>
                ))}
              </ListOptions>
            </ListOptionsWrapper>
          </ListOptionsContainer>
        )}
      </SelectContainer>

      {error && <ErrorMessage>{error}</ErrorMessage>}
    </WrapperSelectContainer>
  );
};

export default memo(Select);
